import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Globe,
  CheckCircle2,
  Clock,
  XCircle,
  MessageCircle,
  Trash2,
  Check,
  Search,
  Settings,
  Phone,
  Calendar,
  Music,
  Heart,
  User,
  Shield,
  ShieldCheck,
  ExternalLink,
  Sparkles,
  RefreshCw,
  Copy,
  AlertCircle,
  FileText,
  Save,
  ChevronRight,
  Eye,
  AlertTriangle,
  X,
  Share2,
  QrCode,
  ArrowRight,
  HelpCircle,
  Sliders,
  Mail,
  KeyRound,
  Lock,
} from 'lucide-react';
import {
  MyWebRequest,
  WebRequestStatus,
  CoupleSettings,
  MyWebConfig,
  getWebRequestPhaseInfo,
} from '../../types';
import { romanticAudio } from '../../utils/audio';
import { PersonalizedCoupleWebsite } from '../PersonalizedCoupleWebsite';
import { REAL_AVAILABLE_FEATURES } from '../MyWebView';

interface RequestModalErrorBoundaryProps {
  children: React.ReactNode;
  onClose: () => void;
}

interface RequestModalErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class RequestModalErrorBoundary extends (React.Component as unknown as {
  new (props: RequestModalErrorBoundaryProps): {
    props: RequestModalErrorBoundaryProps;
    state: RequestModalErrorBoundaryState;
    setState: (
      update:
        | Partial<RequestModalErrorBoundaryState>
        | ((prev: RequestModalErrorBoundaryState) => Partial<RequestModalErrorBoundaryState>)
    ) => void;
  };
}) {
  state: RequestModalErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  static getDerivedStateFromError(error: Error): Partial<RequestModalErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[RequestModalErrorBoundary caught]:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center space-y-4 max-w-md mx-auto my-auto">
          <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-500 mx-auto shadow-sm">
            <AlertCircle className="w-7 h-7" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900">
              No se pudo mostrar el detalle de esta solicitud
            </h3>
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
              Ocurrió un contratiempo técnico al procesar los datos de esta solicitud. La lista de solicitudes y tu sesión de administrador permanecen completamente seguras.
            </p>
          </div>
          <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl text-[11px] font-mono text-slate-700 text-left overflow-x-auto max-h-28">
            <span className="font-semibold text-rose-600 block mb-0.5">Detalle técnico:</span>
            {this.state.error?.message || 'Error desconocido'}
          </div>
          <button
            type="button"
            onClick={() => {
              this.setState({ hasError: false, error: null });
              this.props.onClose();
            }}
            className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs transition-colors cursor-pointer"
          >
            Volver a la lista de solicitudes
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

interface MyWebAdminSectionProps {
  settings: CoupleSettings;
  requests: MyWebRequest[];
  onSaveSettings: (settings: CoupleSettings) => void;
  onUpdateStatus: (
    id: string,
    status: WebRequestStatus,
    adminNotes?: string,
    extraAdminFields?: Partial<MyWebRequest>
  ) => Promise<void>;
  onUpdateRequest?: (updatedRequest: MyWebRequest) => Promise<void> | void;
  onDeleteRequest: (id: string) => Promise<void>;
  triggerToast?: (msg: string) => void;
}

export const MyWebAdminSection: React.FC<MyWebAdminSectionProps> = ({
  settings,
  requests,
  onSaveSettings,
  onUpdateStatus,
  onUpdateRequest,
  onDeleteRequest,
  triggerToast = (_msg: string) => {},
}) => {
  const currentConfig: MyWebConfig = settings.myWebConfig || {
    autoApproval: false,
    whatsappAdminNumber: '',
    whatsappMessageTemplate:
      '¡Hola! Mi solicitud #{code} para nuestra web de pareja con {partner} está lista. Quisiera coordinar los detalles.',
    enabled: true,
  };

  // Approval Mode state & confirmation modal
  const [autoApproval, setAutoApproval] = useState<boolean>(Boolean(currentConfig.autoApproval));
  const [showModeConfirmModal, setShowModeConfirmModal] = useState<boolean>(false);
  const [pendingModeTarget, setPendingModeTarget] = useState<boolean | null>(null);
  const [isUpdatingMode, setIsUpdatingMode] = useState(false);

  // WhatsApp Configuration states
  const [adminPhone, setAdminPhone] = useState<string>(currentConfig.whatsappAdminNumber || '');
  const [msgTemplate, setMsgTemplate] = useState<string>(
    currentConfig.whatsappMessageTemplate ||
      '¡Hola! Mi solicitud #{code} para nuestra web de pareja con {partner} está lista. Quisiera coordinar los detalles.'
  );
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [templateValidationError, setTemplateValidationError] = useState<string | null>(null);
  const [configSuccessMessage, setConfigSuccessMessage] = useState<string | null>(null);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [processingId, setProcessingId] = useState<string | null>(null);

  // Selected Request for Detailed Management Modal
  const [managingRequestId, setManagingRequestId] = useState<string | null>(null);

  // Confirmation Modals State
  const [confirmApproveModal, setConfirmApproveModal] = useState<MyWebRequest | null>(null);
  const [confirmRejectModal, setConfirmRejectModal] = useState<MyWebRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [changesRequestModal, setChangesRequestModal] = useState<MyWebRequest | null>(null);
  const [changesNotes, setChangesNotes] = useState('');
  const [confirmDeleteModal, setConfirmDeleteModal] = useState<MyWebRequest | null>(null);

  // Live preview & QR modals
  const [previewRequest, setPreviewRequest] = useState<MyWebRequest | null>(null);
  const [qrCodeModalRequest, setQrCodeModalRequest] = useState<MyWebRequest | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  // Sync local config states if settings prop changes
  useEffect(() => {
    setAutoApproval(Boolean(settings.myWebConfig?.autoApproval));
    setAdminPhone(settings.myWebConfig?.whatsappAdminNumber || '');
    setMsgTemplate(
      settings.myWebConfig?.whatsappMessageTemplate ||
        '¡Hola! Mi solicitud #{code} para nuestra web de pareja con {partner} está lista. Quisiera coordinar los detalles.'
    );
  }, [settings.myWebConfig]);

  // Find active request being managed
  const activeManagingRequest = useMemo<MyWebRequest | null>(() => {
    if (!managingRequestId) return null;
    return requests.find((r) => r.id === managingRequestId) || null;
  }, [managingRequestId, requests]);

  const [assignedPasswordInput, setAssignedPasswordInput] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  useEffect(() => {
    if (activeManagingRequest) {
      setAssignedPasswordInput(activeManagingRequest.assignedPassword || '');
    } else {
      setAssignedPasswordInput('');
    }
  }, [activeManagingRequest?.id, activeManagingRequest?.assignedPassword]);

  const handleSaveAssignedPassword = async () => {
    if (!activeManagingRequest) return;
    const clean = assignedPasswordInput.trim();
    if (!clean || clean.length < 6) {
      if (triggerToast) triggerToast('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    setIsSavingPassword(true);
    try {
      await fetch('/api/userweb/assign-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: activeManagingRequest.id, password: clean }),
      });
      if (onUpdateRequest) {
        await onUpdateRequest({
          ...activeManagingRequest,
          assignedPassword: clean,
        });
      }
      if (triggerToast) triggerToast('🔑 Contraseña de acceso asignada exitosamente.');
      romanticAudio.playBloomSuccess();
    } catch (err) {
      console.error('Error saving assigned password:', err);
      if (triggerToast) triggerToast('Error al guardar la contraseña.');
    } finally {
      setIsSavingPassword(false);
    }
  };

  // Compute real-time stats directly from Firestore requests
  const stats = useMemo(() => {
    let pending = 0;
    let inReview = 0;
    let approved = 0;
    let rejected = 0;

    requests.forEach((r) => {
      const st = (r.status || 'enviado').toLowerCase();
      if (st === 'enviado' || st === 'recibido' || st === 'pending' || st === 'pendiente') {
        pending++;
      } else if (st === 'en_revision' || st === 'cambios_solicitados') {
        inReview++;
      } else if (st === 'aprobado' || st === 'approved' || st === 'publicado') {
        approved++;
      } else if (st === 'rechazado' || st === 'rejected') {
        rejected++;
      }
    });

    return {
      total: requests.length,
      pending,
      inReview,
      approved,
      rejected,
    };
  }, [requests]);

  // Filter requests based on search query and status tab
  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        r.orderCode?.toLowerCase().includes(q) ||
        r.applicantName?.toLowerCase().includes(q) ||
        r.partnerName?.toLowerCase().includes(q) ||
        r.whatsappPhone?.includes(q) ||
        r.id.toLowerCase().includes(q);

      if (!matchesSearch) return false;

      if (filterStatus === 'all') return true;
      const st = (r.status || 'enviado').toLowerCase();

      if (filterStatus === 'enviadas') return st === 'enviado' || st === 'pending' || st === 'pendiente';
      if (filterStatus === 'recibidas') return st === 'recibido';
      if (filterStatus === 'en_revision') return st === 'en_revision' || st === 'cambios_solicitados';
      if (filterStatus === 'aprobadas') return st === 'aprobado' || st === 'approved' || st === 'publicado';
      if (filterStatus === 'rechazadas') return st === 'rechazado' || st === 'rejected';

      return true;
    });
  }, [requests, searchQuery, filterStatus]);

  // Format date helper
  const formatTimestamp = (isoString?: string) => {
    if (!isoString) return 'Sin fecha';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return 'Recientemente';
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Recientemente';
    }
  };

  // Status visual badge styling (clean light theme)
  const getStatusBadge = (status?: WebRequestStatus) => {
    const st = (status || 'enviado').toLowerCase();

    if (st === 'publicado') {
      return {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200',
        dot: 'bg-emerald-500',
        label: 'Publicado',
      };
    }
    if (st === 'aprobado' || st === 'approved') {
      return {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200',
        dot: 'bg-emerald-500',
        label: 'Aprobado',
      };
    }
    if (st === 'cambios_solicitados') {
      return {
        bg: 'bg-orange-50',
        text: 'text-orange-700',
        border: 'border-orange-200',
        dot: 'bg-orange-500',
        label: 'Cambios solicitados',
      };
    }
    if (st === 'en_revision') {
      return {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        border: 'border-amber-200',
        dot: 'bg-amber-500',
        label: 'En revisión',
      };
    }
    if (st === 'recibido') {
      return {
        bg: 'bg-teal-50',
        text: 'text-teal-700',
        border: 'border-teal-200',
        dot: 'bg-teal-500',
        label: 'Recibido',
      };
    }
    if (st === 'rejected' || st === 'rechazado') {
      return {
        bg: 'bg-rose-50',
        text: 'text-rose-700',
        border: 'border-rose-200',
        dot: 'bg-rose-500',
        label: 'Rechazado',
      };
    }
    return {
      bg: 'bg-slate-100',
      text: 'text-slate-700',
      border: 'border-slate-200',
      dot: 'bg-slate-400',
      label: 'Enviada',
    };
  };

  // Change Approval Mode Handler with Confirmation
  const handleInitiateModeChange = (targetMode: boolean) => {
    if (targetMode === autoApproval) return;
    setPendingModeTarget(targetMode);
    setShowModeConfirmModal(true);
    romanticAudio.playNote(480, 0.08, 'sine', 0.03);
  };

  const handleConfirmModeChange = async () => {
    if (pendingModeTarget === null) return;
    setIsUpdatingMode(true);
    try {
      const updatedConfig: MyWebConfig = {
        ...currentConfig,
        autoApproval: pendingModeTarget,
        whatsappAdminNumber: adminPhone.trim(),
        whatsappMessageTemplate: msgTemplate.trim(),
      };
      await onSaveSettings({
        ...settings,
        myWebConfig: updatedConfig,
      });
      setAutoApproval(pendingModeTarget);
      romanticAudio.playBloomSuccess();
      triggerToast(
        pendingModeTarget
          ? 'Modo cambiado a Aprobación Automática exitosamente.'
          : 'Modo cambiado a Aprobación Manual exitosamente.'
      );
    } catch (err: any) {
      console.error('Error saving approval mode:', err);
      triggerToast('Error al actualizar el modo de aprobación.');
    } finally {
      setIsUpdatingMode(false);
      setShowModeConfirmModal(false);
      setPendingModeTarget(null);
    }
  };

  // Validate message template variables
  const validateTemplate = (tpl: string): boolean => {
    // Only allow {code}, {partner}, {name}
    const matches = tpl.match(/\{([^}]+)\}/g) || [];
    const validVars = ['{code}', '{partner}', '{name}'];
    for (const m of matches) {
      if (!validVars.includes(m)) {
        setTemplateValidationError(`Variable no válida: "${m}". Solo se permiten {code} y {partner}.`);
        return false;
      }
    }
    setTemplateValidationError(null);
    return true;
  };

  // Save WhatsApp settings
  const handleSaveWhatsAppConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateTemplate(msgTemplate)) {
      return;
    }

    setIsSavingConfig(true);
    setConfigSuccessMessage(null);
    try {
      const updatedConfig: MyWebConfig = {
        ...currentConfig,
        autoApproval,
        whatsappAdminNumber: adminPhone.trim(),
        whatsappMessageTemplate: msgTemplate.trim(),
      };
      await onSaveSettings({
        ...settings,
        myWebConfig: updatedConfig,
      });
      romanticAudio.playBloomSuccess();
      setConfigSuccessMessage('Configuración de WhatsApp guardada exitosamente.');
      triggerToast('Ajustes de WhatsApp guardados.');
      setTimeout(() => setConfigSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error saving WhatsApp settings:', err);
      triggerToast('Error al guardar la configuración.');
    } finally {
      setIsSavingConfig(false);
    }
  };

  // Status transitions
  const handleExecuteStatusChange = async (
    id: string,
    newStatus: WebRequestStatus,
    adminNotes?: string,
    extraFields?: Partial<MyWebRequest>
  ) => {
    setProcessingId(id);
    try {
      await onUpdateStatus(id, newStatus, adminNotes, extraFields);
      romanticAudio.playBloomSuccess();
      triggerToast(`Estado actualizado a: ${newStatus}`);
    } catch (err: any) {
      console.error('Error updating request status:', err);
      triggerToast('Error al actualizar el estado de la solicitud.');
    } finally {
      setProcessingId(null);
    }
  };

  // Approve Request Handler
  const handleConfirmApprove = async () => {
    if (!confirmApproveModal) return;
    const req = confirmApproveModal;
    setProcessingId(req.id);
    try {
      await onUpdateStatus(req.id, 'aprobado', undefined, {
        isPublished: true,
      });
      romanticAudio.playBloomSuccess();
      triggerToast(`¡Solicitud #${req.orderCode} aprobada exitosamente!`);
      setConfirmApproveModal(null);
    } catch (err) {
      console.error('Error approving request:', err);
      triggerToast('Error al aprobar la solicitud.');
    } finally {
      setProcessingId(null);
    }
  };

  // Reject Request Handler
  const handleConfirmReject = async () => {
    if (!confirmRejectModal) return;
    const req = confirmRejectModal;
    setProcessingId(req.id);
    try {
      await onUpdateStatus(req.id, 'rechazado', rejectReason.trim() || undefined);
      romanticAudio.playBloomSuccess();
      triggerToast(`Solicitud #${req.orderCode} rechazada.`);
      setConfirmRejectModal(null);
      setRejectReason('');
    } catch (err) {
      console.error('Error rejecting request:', err);
      triggerToast('Error al rechazar la solicitud.');
    } finally {
      setProcessingId(null);
    }
  };

  // Request Changes Handler
  const handleConfirmRequestChanges = async () => {
    if (!changesRequestModal || !changesNotes.trim()) return;
    const req = changesRequestModal;
    setProcessingId(req.id);
    try {
      await onUpdateStatus(req.id, 'cambios_solicitados', changesNotes.trim());
      romanticAudio.playBloomSuccess();
      triggerToast(`Cambios solicitados enviados a #${req.orderCode}.`);
      setChangesRequestModal(null);
      setChangesNotes('');
    } catch (err) {
      console.error('Error requesting changes:', err);
      triggerToast('Error al solicitar cambios.');
    } finally {
      setProcessingId(null);
    }
  };

  // Delete Request Handler
  const handleConfirmDelete = async () => {
    if (!confirmDeleteModal) return;
    const req = confirmDeleteModal;
    setProcessingId(req.id);
    try {
      await onDeleteRequest(req.id);
      romanticAudio.playBloomSuccess();
      triggerToast(`Solicitud #${req.orderCode} eliminada.`);
      setConfirmDeleteModal(null);
      if (managingRequestId === req.id) {
        setManagingRequestId(null);
      }
    } catch (err) {
      console.error('Error deleting request:', err);
      triggerToast('Error al eliminar la solicitud.');
    } finally {
      setProcessingId(null);
    }
  };

  // Helper to open WhatsApp with interpolated message template
  const handleOpenWhatsAppToApplicant = (req: MyWebRequest) => {
    const rawPhone = req.whatsappPhone || '';
    const cleanPhone = rawPhone.replace(/[^0-9]/g, '');
    if (!cleanPhone) {
      triggerToast('El solicitante no tiene un número telefónico válido.');
      return;
    }
    const partner = req.partnerName || 'tu pareja';
    const code = req.orderCode || req.id;
    let message = msgTemplate.replace(/\{code\}/g, code).replace(/\{partner\}/g, partner);
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // Copy share URL
  const handleCopyShareLink = (orderCode: string) => {
    const url = `${window.location.origin}/?web=${encodeURIComponent(orderCode)}`;
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(url);
      setCopiedLink(true);
      romanticAudio.playBloomSuccess();
      setTimeout(() => setCopiedLink(false), 2000);
      triggerToast('Enlace copiado al portapapeles.');
    }
  };

  return (
    <div
      className="space-y-6 font-['Plus_Jakarta_Sans',sans-serif] text-slate-800"
      id="myweb-admin-module"
    >
      {/* ======================================================== */}
      {/* 1. ENCABEZADO LIMPIO & RESUMEN EN TIEMPO REAL            */}
      {/* ======================================================== */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-50 text-rose-600 text-xs font-semibold mb-2">
              <Globe className="w-3.5 h-3.5" />
              <span>Módulo Administrativo</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-['Playfair_Display',serif]">
              Solicitudes Mi Web
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Gestiona las solicitudes de creación, revisión y publicación de sitios web.
            </p>
          </div>

          <div className="flex items-center gap-2 self-start md:self-auto">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 text-slate-600 text-xs font-semibold">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>Firestore en vivo</span>
            </span>
          </div>
        </div>

        {/* Tarjetas de Resumen en Tiempo Real */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-6 pt-6 border-t border-slate-100">
          <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/70">
            <div className="text-xs font-medium text-slate-500">Solicitudes totales</div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</div>
          </div>

          <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/70">
            <div className="text-xs font-medium text-slate-500">Pendientes</div>
            <div className="text-2xl font-bold text-slate-700 mt-1 flex items-center gap-1.5">
              <span>{stats.pending}</span>
              {stats.pending > 0 && <span className="w-2 h-2 rounded-full bg-amber-500" />}
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/70">
            <div className="text-xs font-medium text-slate-500">En revisión</div>
            <div className="text-2xl font-bold text-amber-700 mt-1">{stats.inReview}</div>
          </div>

          <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/70">
            <div className="text-xs font-medium text-slate-500">Aprobadas</div>
            <div className="text-2xl font-bold text-emerald-700 mt-1">{stats.approved}</div>
          </div>

          <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/70 col-span-2 sm:col-span-1">
            <div className="text-xs font-medium text-slate-500">Rechazadas</div>
            <div className="text-2xl font-bold text-rose-700 mt-1">{stats.rejected}</div>
          </div>
        </div>
      </div>

      {/* ======================================================== */}
      {/* 2. CONFIGURACIÓN: MÉTODO DE APROBACIÓN & WHATSAPP        */}
      {/* ======================================================== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Selector de Método de Aprobación */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Sliders className="w-4 h-4 text-rose-500" />
                <span>Método de aprobación</span>
              </h2>

              <span
                className={`text-xs font-bold px-2.5 py-1 rounded-full border ${
                  autoApproval
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-slate-100 text-slate-700 border-slate-200'
                }`}
              >
                ● {autoApproval ? 'Aprobación automática' : 'Aprobación manual'}
              </span>
            </div>

            <p className="text-xs text-slate-500 mb-5 leading-relaxed">
              Define si las nuevas solicitudes se aprueban automáticamente en el sistema o si requieren tu revisión manual.
            </p>

            {/* Selector Profesional */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Opción Manual */}
              <button
                type="button"
                onClick={() => handleInitiateModeChange(false)}
                className={`p-4 rounded-xl border text-left transition-all cursor-pointer ${
                  !autoApproval
                    ? 'bg-rose-50/50 border-rose-300 ring-2 ring-rose-500/20 shadow-xs'
                    : 'bg-slate-50 hover:bg-slate-100/70 border-slate-200 text-slate-600'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                      !autoApproval ? 'border-rose-600 bg-rose-600 text-white' : 'border-slate-300 bg-white'
                    }`}
                  >
                    {!autoApproval && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <span className="text-xs font-bold text-slate-900">Aprobación manual</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-2 pl-6.5 leading-snug">
                  Cada solicitud requiere revisión antes de aprobarse.
                </p>
              </button>

              {/* Opción Automática */}
              <button
                type="button"
                onClick={() => handleInitiateModeChange(true)}
                className={`p-4 rounded-xl border text-left transition-all cursor-pointer ${
                  autoApproval
                    ? 'bg-emerald-50/50 border-emerald-300 ring-2 ring-emerald-500/20 shadow-xs'
                    : 'bg-slate-50 hover:bg-slate-100/70 border-slate-200 text-slate-600'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                      autoApproval ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300 bg-white'
                    }`}
                  >
                    {autoApproval && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <span className="text-xs font-bold text-slate-900">Aprobación automática</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-2 pl-6.5 leading-snug">
                  Las solicitudes válidas se procesan automáticamente.
                </p>
              </button>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-400 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-slate-500" />
            <span>Validado de forma segura en la arquitectura de backend y Firestore.</span>
          </div>
        </div>

        {/* Configuración de WhatsApp y Mensaje de Confirmación */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-xs">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Phone className="w-4 h-4 text-emerald-600" />
              <span>WhatsApp de contacto</span>
            </h2>

            <span
              className={`text-xs font-bold px-2.5 py-1 rounded-full border ${
                adminPhone.trim()
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border-slate-200'
              }`}
            >
              ● {adminPhone.trim() ? 'Configurado' : 'Sin configurar'}
            </span>
          </div>

          <p className="text-xs text-slate-500 mb-4">
            Número utilizado para enlaces y coordinación directa con los solicitantes.
          </p>

          <form onSubmit={handleSaveWhatsAppConfig} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Número de WhatsApp (con código de país)
              </label>
              <input
                type="text"
                placeholder="Ej. +57 321 360 9123"
                value={adminPhone}
                onChange={(e) => setAdminPhone(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-900 text-xs placeholder-slate-400 focus:outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-400 transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Mensaje de confirmación
              </label>
              <textarea
                rows={2}
                value={msgTemplate}
                onChange={(e) => {
                  setMsgTemplate(e.target.value);
                  validateTemplate(e.target.value);
                }}
                className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-900 text-xs placeholder-slate-400 focus:outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-400 transition-colors resize-none"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Variables disponibles: <code className="text-rose-600 font-semibold">{'{code}'}</code> (número de orden), <code className="text-rose-600 font-semibold">{'{partner}'}</code> (nombre de la pareja).
              </p>
              {templateValidationError && (
                <p className="text-xs text-rose-600 mt-1 font-semibold">{templateValidationError}</p>
              )}
              {configSuccessMessage && (
                <p className="text-xs text-emerald-600 mt-1 font-semibold flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" />
                  <span>{configSuccessMessage}</span>
                </p>
              )}
            </div>

            <div className="flex justify-end pt-1">
              <button
                type="submit"
                disabled={isSavingConfig || Boolean(templateValidationError)}
                className="px-4 py-2 rounded-xl bg-rose-500 hover:bg-rose-600 text-white font-bold text-xs flex items-center gap-1.5 transition-colors disabled:opacity-50 cursor-pointer shadow-xs"
              >
                {isSavingConfig ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                <span>Guardar cambios</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ======================================================== */}
      {/* 3. BUSCADOR & FILTROS DE ESTADO                          */}
      {/* ======================================================== */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-xs space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          {/* Campo de Búsqueda */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Buscar por número de orden, nombre o WhatsApp..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-900 text-xs placeholder-slate-400 focus:outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-400 transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer text-xs"
              >
                ✕
              </button>
            )}
          </div>

          <div className="text-xs text-slate-500 font-medium whitespace-nowrap self-end md:self-auto">
            Mostrando <strong>{filteredRequests.length}</strong> de {requests.length} solicitudes
          </div>
        </div>

        {/* Filtros de Estado */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none text-xs">
          {[
            { id: 'all', label: 'Todos' },
            { id: 'enviadas', label: 'Enviadas' },
            { id: 'recibidas', label: 'Recibidas' },
            { id: 'en_revision', label: 'En revisión' },
            { id: 'aprobadas', label: 'Aprobadas' },
            { id: 'rechazadas', label: 'Rechazadas' },
          ].map((tab) => {
            const isActive = filterStatus === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setFilterStatus(tab.id);
                  romanticAudio.playNote(480, 0.05, 'sine', 0.02);
                }}
                className={`px-3 py-1.5 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer border ${
                  isActive
                    ? 'bg-rose-50 text-rose-700 border-rose-200 shadow-2xs'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ======================================================== */}
      {/* 4. LISTA DE SOLICITUDES (TARJETAS LIMPIAS)               */}
      {/* ======================================================== */}
      <div className="space-y-3">
        {filteredRequests.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center text-slate-500 shadow-xs space-y-2">
            <Globe className="w-10 h-10 text-slate-300 mx-auto" />
            <h3 className="text-sm font-bold text-slate-800">No se encontraron solicitudes</h3>
            <p className="text-xs text-slate-400 max-w-sm mx-auto">
              No hay solicitudes que coincidan con los filtros aplicados o aún no se han registrado solicitudes.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRequests.map((req) => {
              const badge = getStatusBadge(req.status);
              return (
                <div
                  key={req.id}
                  className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-xs hover:border-slate-300 hover:shadow-sm transition-all flex flex-col justify-between space-y-4"
                >
                  <div>
                    {/* Header de la Tarjeta */}
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="font-mono text-xs font-bold text-slate-700">
                        #{req.orderCode || 'SIN-CODIGO'}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${badge.bg} ${badge.text} ${badge.border}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                        <span>{badge.label}</span>
                      </span>
                    </div>

                    {/* Nombres de la Pareja */}
                    <h3 className="text-base font-bold text-slate-900 truncate">
                      {req.applicantName} ❤️ {req.partnerName}
                    </h3>

                    {/* Detalles Breves */}
                    <div className="mt-3 space-y-1.5 text-xs text-slate-600">
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-400">WhatsApp:</span>
                        <span className="font-medium text-slate-800">{req.whatsappPhone || 'Sin número'}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-400">Fecha:</span>
                        <span>{formatTimestamp(req.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Botón Ver Solicitud */}
                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => {
                        setManagingRequestId(req.id);
                        try {
                          romanticAudio.playNote(520, 0.08, 'sine', 0.03);
                        } catch {}
                      }}
                      className="w-full py-2 rounded-xl bg-slate-100 hover:bg-rose-50 hover:text-rose-600 text-slate-700 text-xs font-bold border border-slate-200/80 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>Ver solicitud</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ======================================================== */}
      {/* 5. MODAL: DETALLE COMPLETO DE LA SOLICITUD               */}
      {/* ======================================================== */}
      <AnimatePresence>
        {activeManagingRequest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-slate-200 max-w-2xl w-full my-8 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              <RequestModalErrorBoundary onClose={() => setManagingRequestId(null)}>
                {/* Header del Modal */}
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-xs font-bold px-2 py-0.5 rounded-lg bg-slate-200 text-slate-800">
                      #{activeManagingRequest.orderCode}
                    </span>
                    {(() => {
                      const badge = getStatusBadge(activeManagingRequest.status);
                      return (
                        <span
                          className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-0.5 rounded-full border ${badge.bg} ${badge.text} ${badge.border}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                          <span>{badge.label}</span>
                        </span>
                      );
                    })()}
                  </div>
                  <h2 className="text-xl font-bold text-slate-900">
                    {activeManagingRequest.applicantName} &amp; {activeManagingRequest.partnerName}
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={() => setManagingRequestId(null)}
                  className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Contenido scrolleable del detalle */}
              <div className="p-6 overflow-y-auto space-y-6 text-xs text-slate-700">
                {/* Datos de Contacto y Fechas */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/70">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                      WhatsApp de contacto
                    </span>
                    <div className="flex items-center justify-between mt-1">
                      <span className="font-bold text-slate-900 text-sm">
                        {activeManagingRequest.whatsappPhone || 'No proporcionado'}
                      </span>
                      {activeManagingRequest.whatsappPhone && (
                        <button
                          type="button"
                          onClick={() => handleOpenWhatsAppToApplicant(activeManagingRequest)}
                          className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-bold text-[11px] flex items-center gap-1 cursor-pointer"
                        >
                          <MessageCircle className="w-3 h-3" />
                          <span>WhatsApp</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/70">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                      Fecha de registro
                    </span>
                    <span className="font-bold text-slate-900 text-sm mt-1 block">
                      {formatTimestamp(activeManagingRequest.createdAt)}
                    </span>
                  </div>

                  {activeManagingRequest.anniversaryDate && (
                    <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/70">
                      <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                        Fecha especial / Aniversario
                      </span>
                      <span className="font-bold text-slate-900 text-sm mt-1 block">
                        {activeManagingRequest.anniversaryDate}
                      </span>
                    </div>
                  )}

                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/70">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block flex items-center gap-1">
                      <Mail className="w-3 h-3 text-[#E85D8E]" />
                      <span>Correo de acceso</span>
                    </span>
                    <span className="font-bold text-slate-900 text-sm mt-1 block truncate">
                      {activeManagingRequest.email || activeManagingRequest.partner1Email || 'No especificado'}
                    </span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/70 col-span-1 sm:col-span-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                        <KeyRound className="w-3.5 h-3.5 text-[#E85D8E]" />
                        <span>Contraseña de acceso del usuario</span>
                      </span>
                      {activeManagingRequest.assignedPassword ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                          Asignada
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                          Pendiente de asignación
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <input
                        type="text"
                        placeholder="Asigna una contraseña (mínimo 6 caracteres)..."
                        value={assignedPasswordInput}
                        onChange={(e) => setAssignedPasswordInput(e.target.value)}
                        className="flex-1 px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-mono text-slate-800 focus:outline-none focus:border-[#E85D8E] focus:ring-1 focus:ring-[#E85D8E]"
                      />
                      <button
                        type="button"
                        onClick={handleSaveAssignedPassword}
                        disabled={isSavingPassword}
                        className="px-3.5 py-2 rounded-xl bg-[#E85D8E] hover:bg-[#DF4D80] text-white text-xs font-bold transition-colors cursor-pointer shrink-0 disabled:opacity-50"
                      >
                        {isSavingPassword ? 'Guardando...' : 'Guardar contraseña'}
                      </button>
                    </div>
                    <p className="text-[10px] text-slate-500 leading-snug">
                      El usuario utilizará su correo registrado y esta contraseña para ingresar al panel de administración de su sitio web.
                    </p>
                  </div>
                </div>

                {/* Dedicatoria */}
                {activeManagingRequest.dedication && (
                  <div className="p-4 rounded-xl bg-rose-50/40 border border-rose-200/60">
                    <span className="text-[11px] font-bold text-rose-700 uppercase tracking-wider block mb-1">
                      Dedicatoria de portada
                    </span>
                    <p className="text-slate-800 italic">"{activeManagingRequest.dedication}"</p>
                  </div>
                )}

                {/* Observaciones iniciales */}
                {activeManagingRequest.notes && (
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                      Observaciones del solicitante
                    </span>
                    <p className="text-slate-700">{activeManagingRequest.notes}</p>
                  </div>
                )}

                {/* Observaciones del administrador */}
                {activeManagingRequest.adminNotes && (
                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                    <span className="text-[11px] font-bold text-amber-700 uppercase tracking-wider block mb-1">
                      Observación / Motivo registrado por el administrador
                    </span>
                    <p className="text-amber-900">{activeManagingRequest.adminNotes}</p>
                  </div>
                )}

                {/* Respuesta del usuario a cambios & Estado de bloqueo */}
                {activeManagingRequest.changesSubmittedAt && (
                  <div className="p-4 rounded-xl bg-purple-50 border border-purple-200">
                    <div className="flex items-center gap-2 text-purple-900 font-bold mb-1">
                      <Lock className="w-4 h-4 text-purple-700" />
                      <span>El usuario completó y envió los cambios solicitados</span>
                    </div>
                    <p className="text-purple-800 text-[11px] leading-relaxed">
                      El solicitante guardó y envió las correcciones el <strong className="text-purple-950">{formatTimestamp(activeManagingRequest.changesSubmittedAt)}</strong>. La edición del usuario ha sido bloqueada automáticamente para evitar alteraciones mientras se revisa.
                    </p>
                    {activeManagingRequest.userNotes && (
                      <div className="mt-2.5 p-2.5 rounded-lg bg-white/90 border border-purple-200 text-purple-950 font-medium text-[11px]">
                        <span className="font-bold text-purple-900 block mb-0.5">Mensaje o respuesta del usuario:</span>
                        "{activeManagingRequest.userNotes}"
                      </div>
                    )}
                  </div>
                )}

                {/* Módulos solicitados */}
                {activeManagingRequest.desiredFeatures && activeManagingRequest.desiredFeatures.length > 0 && (
                  <div>
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-2">
                      Módulos seleccionados
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {activeManagingRequest.desiredFeatures.map((featId) => {
                        const def = REAL_AVAILABLE_FEATURES.find((f) => f.id === featId);
                        return (
                          <span
                            key={featId}
                            className="px-2.5 py-1 rounded-lg bg-slate-100 border border-slate-200 text-slate-700 font-medium"
                          >
                            {def ? def.label : featId}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Enlace y Acciones si está Aprobado o Publicado */}
                {(activeManagingRequest.status === 'aprobado' ||
                  activeManagingRequest.status === 'approved' ||
                  activeManagingRequest.status === 'publicado') && (
                  <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-emerald-800 flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span>Sitio web interactivo activo</span>
                      </span>
                      <span className="text-[11px] text-emerald-600 font-mono">
                        /?web={activeManagingRequest.orderCode}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => handleCopyShareLink(activeManagingRequest.orderCode)}
                        className="px-3 py-1.5 rounded-lg bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100 font-bold text-xs flex items-center gap-1 cursor-pointer transition-colors"
                      >
                        {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                        <span>{copiedLink ? 'Copiado' : 'Copiar enlace'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPreviewRequest(activeManagingRequest)}
                        className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        <span>Abrir sitio real</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setQrCodeModalRequest(activeManagingRequest)}
                        className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 font-bold text-xs flex items-center gap-1 cursor-pointer transition-colors"
                      >
                        <QrCode className="w-3.5 h-3.5" />
                        <span>Ver QR</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Barra de Acciones Administrativas */}
              <div className="p-5 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteModal(activeManagingRequest)}
                    className="px-3 py-2 rounded-xl text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-200 text-xs font-bold flex items-center gap-1.5 cursor-pointer transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Eliminar</span>
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {/* Si está en recibido / enviado */}
                  {(activeManagingRequest.status === 'recibido' ||
                    activeManagingRequest.status === 'enviado' ||
                    (activeManagingRequest.status as any) === 'pending') && (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          handleExecuteStatusChange(activeManagingRequest.id, 'en_revision')
                        }
                        disabled={processingId === activeManagingRequest.id}
                        className="px-3.5 py-2 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs font-bold transition-colors cursor-pointer"
                      >
                        Iniciar revisión
                      </button>
                      <button
                        type="button"
                        onClick={() => setChangesRequestModal(activeManagingRequest)}
                        className="px-3.5 py-2 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-bold transition-colors cursor-pointer"
                      >
                        Solicitar cambios
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmRejectModal(activeManagingRequest)}
                        className="px-3.5 py-2 rounded-xl bg-rose-100 hover:bg-rose-200 text-rose-800 text-xs font-bold transition-colors cursor-pointer"
                      >
                        Rechazar
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmApproveModal(activeManagingRequest)}
                        className="px-4 py-2 rounded-xl bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold transition-colors cursor-pointer shadow-xs"
                      >
                        Aprobar solicitud
                      </button>
                    </>
                  )}

                  {/* Si está en revisión */}
                  {activeManagingRequest.status === 'en_revision' && (
                    <>
                      <button
                        type="button"
                        onClick={() => setChangesRequestModal(activeManagingRequest)}
                        className="px-3.5 py-2 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-bold transition-colors cursor-pointer"
                      >
                        Solicitar cambios
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmRejectModal(activeManagingRequest)}
                        className="px-3.5 py-2 rounded-xl bg-rose-100 hover:bg-rose-200 text-rose-800 text-xs font-bold transition-colors cursor-pointer"
                      >
                        Rechazar
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmApproveModal(activeManagingRequest)}
                        className="px-4 py-2 rounded-xl bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold transition-colors cursor-pointer shadow-xs"
                      >
                        Aprobar solicitud
                      </button>
                    </>
                  )}

                  {/* Si tiene cambios solicitados */}
                  {activeManagingRequest.status === 'cambios_solicitados' && (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          handleExecuteStatusChange(activeManagingRequest.id, 'en_revision')
                        }
                        className="px-3.5 py-2 rounded-xl bg-sky-100 hover:bg-sky-200 text-sky-900 text-xs font-bold transition-colors cursor-pointer"
                      >
                        Reanudar revisión
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmRejectModal(activeManagingRequest)}
                        className="px-3.5 py-2 rounded-xl bg-rose-100 hover:bg-rose-200 text-rose-800 text-xs font-bold transition-colors cursor-pointer"
                      >
                        Rechazar
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmApproveModal(activeManagingRequest)}
                        className="px-4 py-2 rounded-xl bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold transition-colors cursor-pointer shadow-xs"
                      >
                        Aprobar solicitud
                      </button>
                    </>
                  )}

                  {/* Si está aprobada */}
                  {(activeManagingRequest.status === 'aprobado' ||
                    activeManagingRequest.status === 'approved') && (
                    <>
                      <button
                        type="button"
                        onClick={() => setChangesRequestModal(activeManagingRequest)}
                        className="px-3.5 py-2 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-bold transition-colors cursor-pointer"
                      >
                        Solicitar cambios
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleExecuteStatusChange(activeManagingRequest.id, 'publicado', undefined, {
                            isPublished: true,
                          })
                        }
                        className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-colors cursor-pointer shadow-xs"
                      >
                        Publicar en línea
                      </button>
                    </>
                  )}

                  {/* Si está publicada */}
                  {activeManagingRequest.status === 'publicado' && (
                    <button
                      type="button"
                      onClick={() => setChangesRequestModal(activeManagingRequest)}
                      className="px-3.5 py-2 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-bold transition-colors cursor-pointer"
                    >
                      Solicitar cambios
                    </button>
                  )}

                  {/* Si está rechazada */}
                  {(activeManagingRequest.status === 'rechazado' ||
                    activeManagingRequest.status === 'rejected') && (
                    <button
                      type="button"
                      onClick={() =>
                        handleExecuteStatusChange(activeManagingRequest.id, 'en_revision')
                      }
                      className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 text-white text-xs font-bold transition-all cursor-pointer shadow-xs"
                    >
                      Reabrir a revisión
                    </button>
                  )}
                </div>
              </div>
              </RequestModalErrorBoundary>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ======================================================== */}
      {/* 6. MODAL: CONFIRMACIÓN DE CAMBIO DE MODO                 */}
      {/* ======================================================== */}
      <AnimatePresence>
        {showModeConfirmModal && pendingModeTarget !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-slate-200 max-w-md w-full p-6 shadow-2xl text-center space-y-4"
            >
              <div
                className={`w-12 h-12 rounded-2xl mx-auto flex items-center justify-center ${
                  pendingModeTarget ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                }`}
              >
                <Sliders className="w-6 h-6" />
              </div>

              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {pendingModeTarget
                    ? '¿Cambiar a aprobación automática?'
                    : '¿Cambiar a aprobación manual?'}
                </h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  {pendingModeTarget
                    ? 'Las nuevas solicitudes válidas podrán aprobarse sin revisión manual en el sistema.'
                    : 'Cada solicitud requerirá revisión y aprobación manual antes de ser publicada.'}
                </p>
              </div>

              <div className="flex items-center justify-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModeConfirmModal(false)}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold cursor-pointer transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmModeChange}
                  disabled={isUpdatingMode}
                  className="px-5 py-2.5 rounded-xl bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold cursor-pointer transition-colors shadow-xs"
                >
                  {isUpdatingMode ? 'Guardando...' : 'Confirmar cambio'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ======================================================== */}
      {/* 7. MODAL: CONFIRMACIÓN DE APROBACIÓN                     */}
      {/* ======================================================== */}
      <AnimatePresence>
        {confirmApproveModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-slate-200 max-w-md w-full p-6 shadow-2xl text-center space-y-4"
            >
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>

              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  ¿Aprobar solicitud #{confirmApproveModal.orderCode}?
                </h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Para <strong>{confirmApproveModal.applicantName} &amp; {confirmApproveModal.partnerName}</strong>. El sitio se guardará como aprobado en Firestore y quedará habilitado su enlace interactivo.
                </p>
              </div>

              <div className="flex items-center justify-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setConfirmApproveModal(null)}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold cursor-pointer transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmApprove}
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold cursor-pointer transition-colors shadow-xs"
                >
                  Aprobar solicitud
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ======================================================== */}
      {/* 8. MODAL: RECHAZAR SOLICITUD (CON MOTIVO OPCIONAL)       */}
      {/* ======================================================== */}
      <AnimatePresence>
        {confirmRejectModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-slate-200 max-w-md w-full p-6 shadow-2xl space-y-4"
            >
              <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 mx-auto flex items-center justify-center">
                <XCircle className="w-6 h-6" />
              </div>

              <div className="text-center">
                <h3 className="text-lg font-bold text-slate-900">
                  ¿Rechazar solicitud #{confirmRejectModal.orderCode}?
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Introduce el motivo del rechazo para que el solicitante pueda entender la razón.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Motivo del rechazo
                </label>
                <textarea
                  rows={3}
                  placeholder="Ej. Inconsistencia en los datos o solicitud duplicada..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-900 text-xs focus:outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-400 resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setConfirmRejectModal(null);
                    setRejectReason('');
                  }}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold cursor-pointer transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmReject}
                  className="px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold cursor-pointer transition-colors shadow-xs"
                >
                  Rechazar solicitud
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ======================================================== */}
      {/* 9. MODAL: SOLICITAR CAMBIOS AL USUARIO                   */}
      {/* ======================================================== */}
      <AnimatePresence>
        {changesRequestModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-slate-200 max-w-md w-full p-6 shadow-2xl space-y-4"
            >
              <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 mx-auto flex items-center justify-center">
                <AlertTriangle className="w-6 h-6" />
              </div>

              <div className="text-center">
                <h3 className="text-lg font-bold text-slate-900">
                  Solicitar cambios en #{changesRequestModal.orderCode}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Escribe la observación para que el usuario pueda corregir sus datos desde su pantalla.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Indicación para el usuario <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="Ej. Por favor verifica el correo electrónico o la fecha de aniversario..."
                  value={changesNotes}
                  onChange={(e) => setChangesNotes(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-900 text-xs focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setChangesRequestModal(null);
                    setChangesNotes('');
                  }}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold cursor-pointer transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={!changesNotes.trim()}
                  onClick={handleConfirmRequestChanges}
                  className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs font-bold cursor-pointer transition-colors shadow-xs disabled:opacity-50"
                >
                  Enviar cambios
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ======================================================== */}
      {/* 10. MODAL: CONFIRMACIÓN DE ELIMINACIÓN                   */}
      {/* ======================================================== */}
      <AnimatePresence>
        {confirmDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-slate-200 max-w-md w-full p-6 shadow-2xl text-center space-y-4"
            >
              <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 mx-auto flex items-center justify-center">
                <Trash2 className="w-6 h-6" />
              </div>

              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  ¿Eliminar solicitud #{confirmDeleteModal.orderCode}?
                </h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Esta acción eliminará permanentemente la solicitud de la base de datos Firestore y de cualquier enlace público activo.
                </p>
              </div>

              <div className="flex items-center justify-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setConfirmDeleteModal(null)}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold cursor-pointer transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold cursor-pointer transition-colors shadow-xs"
                >
                  Eliminar permanentemente
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ======================================================== */}
      {/* 11. MODAL: VISTA PREVIA DEL SITIO WEB REAL               */}
      {/* ======================================================== */}
      <AnimatePresence>
        {previewRequest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-slate-900/35 backdrop-blur-md">
            <div className="bg-white border border-[#F0DDE4] rounded-3xl w-full h-[95vh] max-w-4xl flex flex-col overflow-hidden shadow-2xl">
              <div className="p-4 bg-white border-b border-[#F0DDE4] flex items-center justify-between text-[#302B30]">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-[#E83E78]" />
                  <span className="text-xs font-bold font-mono">
                    Vista previa de: #{previewRequest.orderCode}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setPreviewRequest(null)}
                  className="p-1.5 rounded-lg bg-[#FFF4F7] hover:bg-[#FFE8F0] text-[#77717A] hover:text-[#302B30] border border-[#F0DDE4] transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <PersonalizedCoupleWebsite
                  request={previewRequest}
                  isAdmin={true}
                  onBackToApp={() => setPreviewRequest(null)}
                />
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* ======================================================== */}
      {/* 12. MODAL: VISOR DE CÓDIGO QR                            */}
      {/* ======================================================== */}
      <AnimatePresence>
        {qrCodeModalRequest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl border border-slate-200 max-w-sm w-full p-6 shadow-2xl text-center space-y-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <QrCode className="w-4 h-4 text-slate-700" />
                  <span className="text-xs font-bold text-slate-800">
                    Código QR #{qrCodeModalRequest.orderCode}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setQrCodeModalRequest(null)}
                  className="p-1 text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-3 bg-white rounded-2xl border border-slate-200 inline-block shadow-sm">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(
                    `${window.location.origin}/?web=${encodeURIComponent(qrCodeModalRequest.orderCode)}`
                  )}&margin=10`}
                  alt="QR Code"
                  className="w-48 h-48 rounded-lg"
                />
              </div>

              <div className="text-[11px] font-mono text-slate-500 break-all px-2">
                {window.location.origin}/?web={qrCodeModalRequest.orderCode}
              </div>

              <button
                type="button"
                onClick={() => handleCopyShareLink(qrCodeModalRequest.orderCode)}
                className="w-full py-2.5 rounded-xl bg-rose-500 hover:bg-rose-600 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer shadow-xs"
              >
                {copiedLink ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedLink ? '¡Enlace copiado!' : 'Copiar enlace'}</span>
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
